/**
 * SamplePaperBuilder — dedicated UI for CBSE-style Sample Paper generation.
 * Uses the Question Paper backend under-the-hood but scoped strictly to
 * Curriculum Library selections (Book · Chapters · Topics).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { samplePaperAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CurriculumPicker from '@/components/CurriculumPicker';
import { toast } from 'sonner';
import { Home, Sparkles, Save, Download, Wand2, Loader2, FileText, X, ClipboardList } from 'lucide-react';

const LANGS = [
  { v: 'en', l: 'English' }, { v: 'hi', l: 'हिन्दी Hindi' }, { v: 'sa', l: 'संस्कृत Sanskrit' },
  { v: 'fr', l: 'Français French' }, { v: 'es', l: 'Español Spanish' }, { v: 'de', l: 'Deutsch German' }, { v: 'zh', l: '中文 Mandarin' },
];

const SamplePaperBuilder = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [patterns, setPatterns] = useState([]);
  const [form, setForm] = useState({
    title: 'CBSE Sample Paper',
    subject: '', class_name: '', board: 'CBSE', book_name: '',
    max_marks: 80, duration_minutes: 180,
    pattern_key: '', general_instructions: [], sections: [], language: 'en',
    is_draft: true,
  });
  const [scope, setScope] = useState({ book_ids: [], chapter_ids: [], topic_ids: [], scope_summary: '', filters: {} });
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [saved, setSaved] = useState(null);
  const API_BLOB_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    samplePaperAPI.patterns().then(({ data }) => setPatterns(data.patterns || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (id) {
      samplePaperAPI.getOne(id).then(({ data }) => {
        setForm((f) => ({ ...f, ...data }));
        setSaved(data);
      }).catch(() => toast.error('Failed to load'));
    }
  }, [id]);

  // Sync class/subject from picker
  useEffect(() => {
    if (scope.filters?.class_name && !form.class_name) setForm((f) => ({ ...f, class_name: scope.filters.class_name }));
    if (scope.filters?.subject && !form.subject) setForm((f) => ({ ...f, subject: scope.filters.subject }));
    if (scope.filters?.board && form.board !== scope.filters.board) setForm((f) => ({ ...f, board: scope.filters.board }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.filters]);

  const handleGenerate = async () => {
    if (!form.class_name || !form.subject) { toast.error('Select Class + Subject in the Curriculum Picker'); return; }
    if (!scope.chapter_ids.length && !scope.book_ids.length) { toast.error('Select at least one Book or Chapter'); return; }
    setGenerating(true);
    try {
      const { data } = await samplePaperAPI.generate({
        subject: form.subject, class_name: form.class_name, board: form.board,
        book_name: form.book_name || '',
        max_marks: parseInt(form.max_marks) || 80,
        duration_minutes: parseInt(form.duration_minutes) || 180,
        pattern_key: form.pattern_key || null,
        language: form.language,
        library_book_ids: scope.book_ids,
        library_chapter_ids: scope.chapter_ids,
        library_topic_ids: scope.topic_ids,
      });
      setForm((f) => ({
        ...f, sections: data.sections || [],
        general_instructions: data.general_instructions || f.general_instructions,
        max_marks: data.max_marks || f.max_marks,
        duration_minutes: data.duration_minutes || f.duration_minutes,
      }));
      toast.success('Sample paper generated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const handleSave = async (finalize = false) => {
    if (!user?.school_id) { toast.error('Complete your profile first'); navigate('/profile'); return; }
    setSaving(true);
    const payload = {
      ...form,
      is_draft: !finalize,
      library_book_ids: scope.book_ids,
      library_chapter_ids: scope.chapter_ids,
      library_topic_ids: scope.topic_ids,
      curriculum_scope_summary: scope.scope_summary,
    };
    try {
      let result;
      if (saved?.question_paper_id) {
        const { data } = await samplePaperAPI.update(saved.question_paper_id, payload);
        result = data;
      } else {
        const { data } = await samplePaperAPI.save(payload);
        result = data;
        navigate(`/sample-paper/${data.question_paper_id}`, { replace: true });
      }
      setSaved(result);
      toast.success(finalize ? 'Sample paper published!' : 'Draft saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDownload = async (fmt) => {
    if (!saved?.question_paper_id) { toast.error('Save the paper first'); return; }
    setDownloading(fmt);
    try {
      const api = fmt === 'docx' ? samplePaperAPI.downloadDocx : samplePaperAPI.downloadPdf;
      const { data } = await api(saved.question_paper_id, false);
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `${(form.title || 'sample-paper').replace(/[^a-z0-9-_ ]/gi, '').slice(0, 40).trim()}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error('Download failed'); }
    finally { setDownloading(''); }
  };

  const totalQuestions = form.sections.reduce((n, s) => n + (s.questions?.length || 0), 0);
  const inlinePdfUrl = saved?.question_paper_id ? `${API_BLOB_URL}/api/question-papers/${saved.question_paper_id}/pdf/inline?include_answer_key=false` : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50/30">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-orange-200/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="sp-home-btn" variant="ghost" onClick={() => navigate('/dashboard')} className="text-orange-700"><Home className="w-5 h-5 mr-2" /> Home</Button>
          <div className="flex items-center gap-2"><ClipboardList className="w-6 h-6 text-orange-600" /><h1 className="text-xl font-bold">Sample Paper Generator</h1></div>
          <div className="flex gap-2">
            <Button data-testid="sp-save-draft-btn" variant="outline" onClick={() => handleSave(false)} disabled={saving} className="border-orange-200">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Draft'}
            </Button>
            <Button data-testid="sp-publish-btn" onClick={() => handleSave(true)} disabled={saving || form.sections.length === 0} className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">
              Publish
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        <CurriculumPicker onChange={setScope} defaultBoard="CBSE" defaultSession="2025-26" />

        <Card className="p-6 border border-orange-200/60 bg-white shadow-sm">
          <h2 className="text-lg font-bold text-orange-800 mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5" /> Paper Setup</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Title</Label>
              <Input data-testid="sp-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Max Marks</Label>
              <Input data-testid="sp-max-marks-input" type="number" min="10" max="200" value={form.max_marks} onChange={(e) => setForm({ ...form, max_marks: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Duration (min)</Label>
              <Input data-testid="sp-duration-input" type="number" min="30" max="360" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>CBSE Sample Paper Pattern</Label>
              <Select value={form.pattern_key} onValueChange={(v) => setForm({ ...form, pattern_key: v })}>
                <SelectTrigger data-testid="sp-pattern-select" className="mt-1"><SelectValue placeholder="Auto-suggest based on class/subject" /></SelectTrigger>
                <SelectContent className="max-h-80">
                  {patterns.map((p) => <SelectItem key={p.key} value={p.key}>{p.label} — {p.max_marks} marks</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Language</Label>
              <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                <SelectTrigger data-testid="sp-language-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{LANGS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          {scope.scope_summary && (
            <p className="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2" data-testid="sp-scope-tag">
              🎯 <strong>Scope Lock:</strong> {scope.scope_summary}
            </p>
          )}
        </Card>

        <div className="flex justify-center">
          <Button
            data-testid="sp-generate-btn"
            onClick={handleGenerate}
            disabled={generating || (!scope.chapter_ids.length && !scope.book_ids.length)}
            className="bg-gradient-to-r from-orange-600 to-amber-600 text-white text-base px-8 py-6 shadow-lg">
            <Wand2 className={`w-5 h-5 mr-2 ${generating ? 'animate-spin' : ''}`} />
            {generating ? 'Generating (30-60s)…' : 'Generate Sample Paper with AI'}
          </Button>
        </div>

        {form.sections.length > 0 && (
          <Card className="p-6 border border-orange-200/60 bg-white shadow-sm" data-testid="sp-preview-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <h2 className="text-lg font-bold text-orange-800">Generated Sample Paper</h2>
                <p className="text-sm text-stone-600">{form.sections.length} sections · {totalQuestions} questions · {form.max_marks} marks</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {saved?.question_paper_id && (
                  <Button data-testid="sp-preview-btn" variant="outline" onClick={() => setShowPreview(true)} className="border-orange-200"><FileText className="w-4 h-4 mr-2" /> Preview</Button>
                )}
                <Button data-testid="sp-download-docx-btn" variant="outline" onClick={() => handleDownload('docx')} disabled={!saved || downloading === 'docx'} className="border-orange-200">
                  {downloading === 'docx' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} DOCX
                </Button>
                <Button data-testid="sp-download-pdf-btn" variant="outline" onClick={() => handleDownload('pdf')} disabled={!saved || downloading === 'pdf'} className="border-orange-200">
                  {downloading === 'pdf' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} PDF
                </Button>
              </div>
            </div>
            <div className="space-y-6">
              {form.sections.map((sec, sIdx) => (
                <div key={sIdx} className="border-l-4 border-orange-500 pl-4">
                  <h3 className="font-bold text-orange-700">{sec.name}</h3>
                  <p className="text-xs text-stone-500 mb-2">{sec.description} · {sec.marks_per_question}M each</p>
                  <div className="space-y-2">
                    {(sec.questions || []).map((q, qIdx) => (
                      <div key={qIdx} className="text-sm bg-stone-50 rounded p-3 border border-stone-200" data-testid={`sp-question-${sIdx}-${qIdx}`}>
                        <div className="flex justify-between font-medium mb-1">
                          <span>Q{q.number}.</span>
                          <span className="text-orange-700 text-xs">[{q.marks || sec.marks_per_question} marks]</span>
                        </div>
                        {q.stem && <p className="italic text-xs text-stone-700 mb-1">{q.stem}</p>}
                        <p className="text-sm">{q.question}</p>
                        {q.options && <ul className="text-xs text-stone-700 ml-4 mt-1">{q.options.map((o, i) => <li key={i}>{o}</li>)}</ul>}
                        {q.answer && <p className="mt-1 text-xs text-emerald-700"><strong>Answer:</strong> {q.answer}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </main>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-6xl h-[90vh] p-0" data-testid="sp-preview-modal">
          <DialogHeader className="p-4 border-b flex-row items-center justify-between">
            <DialogTitle>{form.title}</DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}><X className="w-4 h-4" /></Button>
          </DialogHeader>
          {inlinePdfUrl && <iframe title="Sample Paper" src={inlinePdfUrl} className="w-full h-full border-0" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SamplePaperBuilder;
